import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ManagedFormHelper } from 'ehs-react';
import {
  intl,
  intlFmt,
  Maskable,
  showError,
  TextInput,
  Tool,
  useManagedForm,
  yup
} from 'ehs-react';
import { usePortletStore } from '../App';
import {
  checkUserState,
  triggerEmailVerification,
  updateEmailAndPhoneNumber
} from '../stores/ajax';
import type { ccModel } from 'generated-ts-common';

type EmailValidationConfiguration = ccModel.portal.config.validation.EmailValidationConfiguration;
type PhoneValidationConfiguration = ccModel.portal.config.validation.PhoneValidationConfiguration;

export const getYupSchema = (mandatory: boolean) => {
  if (mandatory) {
    return yup.string().defined();
  } else {
    return yup.string().optional();
  }
};
export const getPatternForAnyInput = (mandatory: boolean) => {
  return new RegExp(mandatory ? '.+' : '.*');
};

export const emailValidationSchema = (config: EmailValidationConfiguration) =>
  getYupSchema(true).matches(
    config.enabled
      ? new RegExp(config.regex)
      : new RegExp(getPatternForAnyInput(true)),
    intlFmt('view.email.invalid', config.examples)
  );

const getPatternRegexForMandatoryFlag = (mandatory: boolean, regex: string) => {
  return mandatory ? regex : '^(?=\\s*$)|' + regex;
};

export const mobilePhoneValidationSchema = (
  config: PhoneValidationConfiguration,
  mandatory: boolean
) =>
  getYupSchema(mandatory).matches(
    config.enabled
      ? new RegExp(getPatternRegexForMandatoryFlag(mandatory, config.regex))
      : new RegExp(getPatternForAnyInput(mandatory)),
    intlFmt('view.phone.invalid', config.examples)
  );

export const EmailField = ({
  managedForm,
  example
}: Readonly<{
  managedForm: ManagedFormHelper<any>;
  example: string;
}>): JSX.Element => (
  <div className="input-email-container">
    <TextInput
      required={true}
      label={intl('view.patient.email')}
      placeholder={example}
      type="text"
      {...managedForm.getField('email')}
      error={
        !managedForm.isSubmitting &&
        managedForm.touched.email &&
        managedForm.errors.email
          ? intlFmt('view.email.invalid', example)
          : ''
      }
    />
  </div>
);

export const PhoneField = ({
  managedForm,
  example,
  phoneNumberInputMandatory
}: {
  managedForm: ManagedFormHelper<any>;
  example: string;
  phoneNumberInputMandatory: boolean;
}): JSX.Element => (
  <div className="input-phone-container">
    <TextInput
      required={phoneNumberInputMandatory}
      label={intl('view.patient.mobilePhoneNumber')}
      placeholder={example}
      type="text"
      {...managedForm.getField('phoneNumber')}
      error={
        !managedForm.isSubmitting &&
        managedForm.touched.phoneNumber &&
        managedForm.errors.phoneNumber
          ? intlFmt('view.phone.invalid', example)
          : ''
      }
    />
  </div>
);

export function CompleteUserAccountPage(): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [isActivationState, setActivationState] = useState(false);
  const [
    referenceUserEmail,
    referenceUserPhoneNumber,
    emailValidationConfiguration,
    phoneNumberValidationConfiguration,
    phoneNumberInputMandatory,
    onboardingCompleteUrl,
    apiRequestUrl
  ] = usePortletStore(state => [
    state.configuration.referenceUserEmail,
    state.configuration.referenceUserPhoneNumber,
    state.configuration.emailValidationConfiguration,
    state.configuration.phoneValidationConfiguration,
    state.configuration.phoneNumberInputMandatory,
    state.configuration.onboardingCompleteUrl,
    state.url
  ]);

  const verifyEmailAddress = () => {
    triggerEmailVerification({
      url: apiRequestUrl,
      setLoading: visible => setLoading(visible)
    }).catch(showError);
  };

  const checkUserActiveStateAndRedirect = useCallback(() => {
    if (!isActivationState || document.hidden) {
      return;
    }
    checkUserState({
      url: apiRequestUrl
    }).then(userState => {
      if (userState.success && userState.data) {
        console.log('User became active - redirecting to login page...');
        window.location.href = onboardingCompleteUrl;
      }
    });
  }, [isActivationState, apiRequestUrl, onboardingCompleteUrl]);

  const managedForm = useManagedForm<{ email?: string; phoneNumber?: string }>({
    validateOnChange: true,
    validateOnMount: true,
    initialValues: {
      email: referenceUserEmail,
      phoneNumber: referenceUserPhoneNumber
    },
    validationSchema: yup
      .object()
      .shape({
        email: emailValidationSchema(emailValidationConfiguration),
        phoneNumber: mobilePhoneValidationSchema(
          phoneNumberValidationConfiguration,
          phoneNumberInputMandatory
        )
      })
      .required(),
    onSubmit(values): void | Promise<void> {
      return updateEmailAndPhoneNumber({
        url: apiRequestUrl,
        email: values.email,
        phoneNumber: values.phoneNumber,
        setLoading: visible => setLoading(visible)
      })
        .then(() => {
          setActivationState(true);
          verifyEmailAddress();
        })
        .catch(showError);
    }
  });

  const documentRef = useRef(document);
  useEffect(() => {
    let interval: number;
    const resetInterval = () => {
      clearInterval(interval);
      interval = (setTimeout(() => {
        try {
          checkUserActiveStateAndRedirect();
        } finally {
          resetInterval();
        }
      }, 5000) as unknown) as number;
    };

    // immediately check if a user activates the current tab (or mobile app)
    const currentDocument = documentRef.current;
    currentDocument.addEventListener('visibilitychange', () => {
      checkUserActiveStateAndRedirect();
      resetInterval();
    });

    resetInterval();
    return () => {
      clearInterval(interval);
      currentDocument.removeEventListener(
        'visibilitychange',
        checkUserActiveStateAndRedirect
      );
    };
  }, [checkUserActiveStateAndRedirect]);

  return (
    <Maskable masked={loading} className="welcome-load-mask">
      <div className="complete-account-container s-font">
        <div className="complete-account-header">
          {isActivationState && (
            <Tool
              icon="arrow-left"
              onClick={() => setActivationState(false)}
              tooltip={intl('view.button.back')}
            />
          )}
          <h1 className="complete-account-header-text font-18 center sh-font">
            {intl(
              'patientSelfOnboardingPortlet.contactValidation.completeAccount.title'
            )}
          </h1>
        </div>
        {isActivationState && (
          <div className="complete-account-content">
            <h2 className="complete-account-header-text font-22 sh-font">
              {intl(
                'patientSelfOnboardingPortlet.contactValidation.activateAccount.title'
              )}
            </h2>
            <div className="complete-account-content-description">
              <p className="complete-account-input-hint-check-email font-16">
                {intl(
                  'patientSelfOnboardingPortlet.contactValidation.checkEmail'
                )}
              </p>
              <p className="complete-account-input-hint-send-link font-16">
                {intl(
                  'patientSelfOnboardingPortlet.contactValidation.linkSent'
                )}{' '}
                <b>{managedForm.values.email}</b>
                <br />
                <br />
                {intl(
                  'patientSelfOnboardingPortlet.contactValidation.followLink'
                )}
                <br />
                {intl(
                  'patientSelfOnboardingPortlet.contactValidation.autoRedirect'
                )}
              </p>
              <p className="complete-account-input-hint-send-again font-16">
                {intl(
                  'patientSelfOnboardingPortlet.contactValidation.noLinkReceived'
                )}{' '}
                <a
                  className="complete-account-verification-send-again-link clickable-element"
                  onClick={verifyEmailAddress}
                >
                  {intl(
                    'patientSelfOnboardingPortlet.contactValidation.sendLinkAgain'
                  )}
                </a>
              </p>
            </div>
          </div>
        )}
        {!isActivationState && (
          <div className="complete-account-content">
            <div className="complete-account-content-description">
              <div className="complete-account-image-container">
                <img
                  alt=""
                  className="contact-validation-image"
                  src="/o/PatientSelfOnboardingPortlet/images/complete_user_account.svg"
                  role="none"
                />
              </div>
              <div>
                <h2 className="complete-account-header-text font-22 sh-font">
                  {intl(
                    'patientSelfOnboardingPortlet.contactValidation.contactInformation.title'
                  )}
                </h2>
                <p className="complete-account-description-text font-16">
                  {intl(
                    'patientSelfOnboardingPortlet.contactValidation.addMailandPhone'
                  )}
                </p>
              </div>
            </div>
            <form
              {...managedForm.getFormProps()}
              aria-label={intl(
                'patientSelfOnboardingPortlet.contactValidation.activateAccount.title'
              )}
            >
              <div className="input-form-fields">
                <EmailField
                  managedForm={managedForm}
                  example={emailValidationConfiguration.examples}
                />
                <PhoneField
                  managedForm={managedForm}
                  example={phoneNumberValidationConfiguration.examples}
                  phoneNumberInputMandatory={phoneNumberInputMandatory}
                />
                <div className="content-button-bar">
                  {managedForm.getSubmitButton({
                    disableIfNotValid: true,
                    buttonScale: 'medium',
                    text: intl('common.continue')
                  })}
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    </Maskable>
  );
}
